import { LoginOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { logout as outLogin } from '@/services/auth';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
	menu?: boolean;
	children?: React.ReactNode;
};

export const AvatarName = () => {
	const { initialState } = useModel('@@initialState');
	const { currentUser } = initialState || {};
	return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
	return {
		action: {
			display: 'flex',
			height: '48px',
			marginLeft: 'auto',
			overflow: 'hidden',
			alignItems: 'center',
			padding: '0 8px',
			cursor: 'pointer',
			borderRadius: token.borderRadius,
			'&:hover': {
				backgroundColor: token.colorBgTextHover
			}
		}
	};
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {
	/**
	 * 退出登录，并且将当前的 url 保存
	 */
	const loginOut = async () => {
		try {
			await outLogin();
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			// Token 由后端 Cookie 管理，无需手动清除

			const { search, pathname } = window.location;
			const urlParams = new URL(window.location.href).searchParams;
			const searchParams = new URLSearchParams({
				redirect: pathname + search
			});
			/** 此方法会跳转到 redirect 参数所在的位置 */
			const redirect = urlParams.get('redirect');
			// Note: There may be security issues, please note
			if (window.location.pathname !== '/user/login' && !redirect) {
				history.replace({
					pathname: '/user/login',
					search: searchParams.toString()
				});
			}
		}
	};
	const { styles } = useStyles();

	const { initialState, setInitialState } = useModel('@@initialState');

	const onMenuClick: MenuProps['onClick'] = (event) => {
		const { key } = event;
		if (key === 'logout') {
			flushSync(() => {
				setInitialState((s) => ({ ...s, currentUser: undefined }));
			});
			loginOut();
			return;
		}
		// 如果 key 是以 / 开头，则直接跳转
		if (key.startsWith('/')) {
			history.push(key);
			return;
		}
		history.push(`/account/${key}`);
	};

	const loading = (
		<span className={styles.action}>
			<Spin
				size="small"
				style={{
					marginLeft: 8,
					marginRight: 8
				}}
			/>
		</span>
	);

	if (!initialState) {
		return loading;
	}

	const { currentUser } = initialState;

	if (!currentUser || !currentUser.name) {
		return loading;
	}

	const menuItems = [
		...(menu
			? [
					{
						key: '/system/admin',
						icon: <UserOutlined />,
						label: '个人中心'
					},
					{
						key: '/system/login-logs',
						icon: <LoginOutlined />,
						label: '登录日志'
					},
					{
						type: 'divider' as const
					}
				]
			: []),
		{
			key: 'logout',
			icon: <LogoutOutlined />,
			label: '退出登录'
		}
	];

	return (
		<HeaderDropdown
			menu={{
				selectedKeys: [],
				onClick: onMenuClick,
				items: menuItems
			}}
		>
			<span className={styles.action}>{children}</span>
		</HeaderDropdown>
	);
};
